import { useMemo, type ComponentType } from "react";
import { useParams } from "react-router";

// Next 16 App Router passes `params: Promise<{id: string}>` to dynamic
// route page components, which the page unwraps via React's `use()`
// hook. React Router has no equivalent — params come in synchronously
// via `useParams()`. This adapter bridges the two: it grabs the params
// off react-router, wraps them in an already-resolved Promise, and
// hands the result to the underlying Next-shaped page component.
//
// `use(Promise.resolve(x))` is a documented React 19 escape hatch and
// returns `x` synchronously, so wrapping costs one allocation per
// render and stays consistent across re-renders by memoising on the
// param values.
export function withParams<T extends Record<string, string>>(
  Page: ComponentType<{ params: Promise<T> }>,
) {
  return function WithParams() {
    const params = useParams() as T;
    // useMemo keyed by the JSON of params keeps the Promise identity
    // stable across renders so `use()` resolves the same cached entry
    // instead of suspending again. The dep is the JSON string, not the
    // params object — useParams() returns a fresh object every render
    // and we only want to invalidate the promise when the values
    // actually change. Extracting the key into its own variable keeps
    // the exhaustive-deps lint happy.
    const paramsKey = JSON.stringify(params);
    const promise = useMemo(
      () => Promise.resolve(params),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [paramsKey],
    );
    return <Page params={promise} />;
  };
}
